/**
 * 데이터 변환 유틸리티
 *
 * Mock 데이터를 Google Sheets 행 형식으로 변환합니다.
 */

import type { Category } from '@/domains/category/types/Category'
import type { PaymentMethod } from '@/domains/payment/types/PaymentMethod'
import type { User } from '@/domains/user/types/User'
import type { Budget } from '@/domains/budget/types/Budget'
import type { Transaction } from '@/domains/transaction/types/Transaction'

/**
 * Category 배열을 Google Sheets 행으로 변환
 *
 * 계층 구조(children)를 Flat하게 변환하여 모든 카테고리를 개별 행으로 반환
 *
 * @param categories - 대분류 카테고리 배열 (children 포함)
 * @returns 시트 행 배열 (헤더 제외)
 *
 * @example
 * ```typescript
 * const rows = convertCategoriesToSheetRows(expenseCategories)
 * // [
 * //   ['cat_food', '식비', '🍚', 'expense', 'major', '', '1', 'true', 'true'],
 * //   ['cat_food_meal', '밥', '🍱', 'expense', 'minor', 'cat_food', '1', 'true', 'true'],
 * //   ...
 * // ]
 * ```
 */
export function convertCategoriesToSheetRows(
  categories: Category[]
): string[][] {
  const rows: string[][] = []

  for (const major of categories) {
    // 대분류 추가
    rows.push([
      major.id,
      major.name,
      major.icon,
      major.type,
      major.level,
      major.parentId || '', // null → 빈 문자열
      major.sortOrder.toString(),
      major.isDefault.toString(),
      major.isActive.toString(),
    ])

    // 소분류 추가 (children 배열이 있는 경우)
    if (major.children && major.children.length > 0) {
      for (const minor of major.children) {
        rows.push([
          minor.id,
          minor.name,
          minor.icon,
          minor.type,
          minor.level,
          minor.parentId || '',
          minor.sortOrder.toString(),
          minor.isDefault.toString(),
          minor.isActive.toString(),
        ])
      }
    }
  }

  return rows
}

/**
 * PaymentMethod 배열을 Google Sheets 행으로 변환
 *
 * @param paymentMethods - 결제수단 배열
 * @returns 시트 행 배열 (헤더 제외)
 *
 * @example
 * ```typescript
 * const rows = convertPaymentMethodsToSheetRows(mockPaymentMethods)
 * // [
 * //   ['pm_hong_shinhan', '신한 체크카드', 'debit_card', 'user_001', '주거래 카드', '0', 'true'],
 * //   ...
 * // ]
 * ```
 */
export function convertPaymentMethodsToSheetRows(
  paymentMethods: PaymentMethod[]
): string[][] {
  return paymentMethods.map((pm) => [
    pm.id,
    pm.name,
    pm.type,
    pm.ownerId,
    pm.memo || '', // null/undefined → 빈 문자열
    '0', // sortOrder (기본값, PaymentMethod 타입에 없으므로 기본값 사용)
    pm.isActive.toString(),
  ])
}

/**
 * User 배열을 Google Sheets 행으로 변환
 *
 * @param users - 사용자 배열
 * @returns 시트 행 배열 (헤더 제외)
 *
 * @example
 * ```typescript
 * const rows = convertUsersToSheetRows(mockUsers)
 * // [
 * //   ['user_001', 'hong@gmail.com', '홍길동', 'owner', 'true', '2024-01-01T00:00:00'],
 * //   ...
 * // ]
 * ```
 */
export function convertUsersToSheetRows(users: User[]): string[][] {
  return users.map((user) => [
    user.id,
    user.email,
    user.name,
    user.role,
    user.isActive.toString(),
    user.joinedAt || new Date().toISOString(), // joinedAt 없으면 현재 시각
  ])
}

/**
 * Budget 배열을 Google Sheets 행으로 변환
 *
 * @param budgets - 예산 배열
 * @returns 시트 행 배열 (헤더 제외)
 *
 * @example
 * ```typescript
 * const rows = convertBudgetsToSheetRows(mockBudgets)
 * // [
 * //   ['budget_001', 'cat_food', '', '500000', '2025', '12', 'true'],
 * //   ['budget_001_01', 'cat_food_meal', 'budget_001', '300000', '2025', '12', 'true'],
 * //   ...
 * // ]
 * ```
 */
export function convertBudgetsToSheetRows(budgets: Budget[]): string[][] {
  return budgets.map((budget) => [
    budget.id,
    budget.categoryId,
    budget.parentBudgetId || '', // 대분류는 빈 문자열, 소분류는 부모 예산 ID
    budget.amount.toString(),
    budget.year?.toString() || '', // null이면 빈 문자열
    budget.month?.toString() || '', // null이면 빈 문자열
    budget.isActive.toString(),
  ])
}

/**
 * Transaction 배열을 Google Sheets 행으로 변환
 *
 * @param transactions - 거래 배열
 * @returns 시트 행 배열 (헤더 제외)
 *
 * @example
 * ```typescript
 * const rows = convertTransactionsToSheetRows(mockTransactions)
 * // [
 * //   ['tx_001', '2025-11-28', 'expense', 'cat_food', 'cat_food_meal', '8500', 'pm_hong_shinhan', 'user_001', 'user_001', '점심 김치찌개', '2025-11-28T12:00:00', '2025-11-28T12:00:00'],
 * //   ...
 * // ]
 * ```
 */
export function convertTransactionsToSheetRows(transactions: Transaction[]): string[][] {
  return transactions.map((tx) => [
    tx.id,
    tx.date,
    tx.type,
    tx.majorCategoryId,
    tx.minorCategoryId || '', // null이면 빈 문자열
    tx.amount.toString(),
    tx.paymentMethodId,
    tx.spenderId,
    tx.writerId,
    tx.memo || '', // null/undefined이면 빈 문자열
    tx.createdAt,
    tx.updatedAt,
  ])
}
